import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { UploadIcon, PlusIcon, TrashIcon } from 'lucide-react';
const ExistingEstateDocuments = () => {
  const {
    register,
    watch
  } = useFormContext();
  const hasExistingWill = watch('existingDocuments.hasWill');
  const [primaryTrustees, setPrimaryTrustees] = useState([{
    id: 1
  }]);
  const [alternateTrustees, setAlternateTrustees] = useState([{
    id: 1
  }]);
  const addPrimaryTrustee = () => {
    const newId = primaryTrustees.length > 0 ? Math.max(...primaryTrustees.map(t => t.id)) + 1 : 1;
    setPrimaryTrustees([...primaryTrustees, {
      id: newId
    }]);
  };
  const removePrimaryTrustee = (id: number) => {
    setPrimaryTrustees(primaryTrustees.filter(t => t.id !== id));
  };
  const addAlternateTrustee = () => {
    const newId = alternateTrustees.length > 0 ? Math.max(...alternateTrustees.map(t => t.id)) + 1 : 1;
    setAlternateTrustees([...alternateTrustees, {
      id: newId
    }]);
  };
  const removeAlternateTrustee = (id: number) => {
    setAlternateTrustees(alternateTrustees.filter(t => t.id !== id));
  };
  return <div className="space-y-8" data-id="element-638">
      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-6" data-id="element-639">
        <p className="text-slate-700" data-id="element-640">
          Please provide information about any existing estate planning
          documents you may have.
        </p>
      </div>

      {/* Existing Will */}
      <div className="space-y-4" data-id="element-641">
        <h3 className="text-lg font-medium text-navy-900 border-b border-slate-200 pb-2" data-id="element-642">
          Existing Will
        </h3>

        <div data-id="element-643">
          <label className="block text-navy-800 font-medium mb-2" data-id="element-644">
            Do you have an existing Will (including in other jurisdictions)?
          </label>
          <div className="flex items-center space-x-4 mb-4" data-id="element-645">
            <label className="inline-flex items-center" data-id="element-646">
              <input type="radio" className="form-radio h-5 w-5 text-navy-600" value="true" {...register('existingDocuments.hasWill')} data-id="element-647" />
              <span className="ml-2" data-id="element-648">Yes</span>
            </label>
            <label className="inline-flex items-center" data-id="element-649">
              <input type="radio" className="form-radio h-5 w-5 text-navy-600" value="false" {...register('existingDocuments.hasWill')} data-id="element-650" />
              <span className="ml-2" data-id="element-651">No</span>
            </label>
          </div>

          {hasExistingWill === 'true' && <>
              <div className="mb-4" data-id="element-652">
                <label className="block text-navy-800 font-medium mb-2" data-id="element-653">
                  In which jurisdiction(s) do you have an existing Will?
                </label>
                <input type="text" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500" placeholder="e.g., Ontario, British Columbia, United States" {...register('existingDocuments.willJurisdictions')} data-id="element-654" />
              </div>

              <div data-id="element-655">
                <label className="block text-navy-800 font-medium mb-2" data-id="element-656">
                  Upload existing Will (optional)
                </label>
                <div className="border-2 border-dashed border-slate-300 rounded-md p-4 text-center" data-id="element-657">
                  <UploadIcon className="mx-auto h-12 w-12 text-slate-400" data-id="element-658" />
                  <p className="mt-2 text-sm text-slate-600" data-id="element-659">
                    Drag and drop your file here, or click to select
                  </p>
                  <input type="file" className="hidden" id="will-upload" {...register('existingDocuments.willFile')} data-id="element-660" />
                  <button type="button" onClick={() => document.getElementById('will-upload')?.click()} className="mt-2 px-4 py-2 bg-navy-700 text-white text-sm rounded-md hover:bg-navy-800" data-id="element-661">
                    Select File
                  </button>
                </div>
                <p className="text-sm text-slate-500 mt-2" data-id="element-662">
                  Accepted file formats: PDF, DOC, DOCX, JPG, PNG (Max size:
                  10MB)
                </p>
              </div>
            </>}
        </div>
      </div>

      {/* Primary Estate Trustees */}
      <div className="space-y-4" data-id="element-663">
        <div className="flex justify-between items-center" data-id="element-664">
          <h3 className="text-lg font-medium text-navy-900 border-b border-slate-200 pb-2" data-id="element-665">
            Primary Estate Trustee(s)
          </h3>
          <button type="button" onClick={addPrimaryTrustee} className="flex items-center text-sm text-navy-700 hover:text-navy-900" data-id="element-666">
            <PlusIcon size={16} className="mr-1" data-id="element-667" />
            Add Trustee
          </button>
        </div>

        <div className="bg-blue-50 p-3 rounded-md border border-blue-200 mb-4" data-id="element-668">
          <p className="text-sm text-blue-800" data-id="element-669">
            You may name one or more Primary Estate Trustees (also called
            Executors). They will be responsible for administering your estate
            after your death.
          </p>
        </div>

        {primaryTrustees.map((trustee, index) => <div key={trustee.id} className="p-4 bg-slate-50 rounded-md" data-id="element-670">
            <div className="flex justify-between items-center mb-3" data-id="element-671">
              <h4 className="font-medium" data-id="element-672">Primary Trustee {index + 1}</h4>
              {primaryTrustees.length > 1 && <button type="button" onClick={() => removePrimaryTrustee(trustee.id)} className="text-red-500 hover:text-red-700" data-id="element-673">
                  <TrashIcon size={16} data-id="element-674" />
                </button>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-675">
              <div data-id="element-676">
                <label className="block text-navy-800 font-medium mb-2" data-id="element-677">
                  Name
                </label>
                <input type="text" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500" placeholder="Enter trustee's name" {...register(`estateTrustees.primary.${index}.name`)} data-id="element-678" />
              </div>
              <div data-id="element-679">
                <label className="block text-navy-800 font-medium mb-2" data-id="element-680">
                  Relationship
                </label>
                <input type="text" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500" placeholder="e.g., Spouse, Child, Friend" {...register(`estateTrustees.primary.${index}.relationship`)} data-id="element-681" />
              </div>
              <div data-id="element-682">
                <label className="block text-navy-800 font-medium mb-2" data-id="element-683">
                  City
                </label>
                <input type="text" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500" placeholder="Enter city" {...register(`estateTrustees.primary.${index}.city`)} data-id="element-684" />
              </div>
            </div>
          </div>)}
      </div>

      {/* Alternate Estate Trustees */}
      <div className="space-y-4" data-id="element-685">
        <div className="flex justify-between items-center" data-id="element-686">
          <h3 className="text-lg font-medium text-navy-900 border-b border-slate-200 pb-2" data-id="element-687">
            Alternate Estate Trustee(s)
          </h3>
          <button type="button" onClick={addAlternateTrustee} className="flex items-center text-sm text-navy-700 hover:text-navy-900" data-id="element-688">
            <PlusIcon size={16} className="mr-1" data-id="element-689" />
            Add Trustee
          </button>
        </div>

        <div className="bg-blue-50 p-3 rounded-md border border-blue-200 mb-4" data-id="element-690">
          <p className="text-sm text-blue-800" data-id="element-691">
            Alternate Trustees act if your Primary Trustee(s) are unable or
            unwilling to serve. You may name one or more alternates.
          </p>
        </div>

        {alternateTrustees.map((trustee, index) => <div key={trustee.id} className="p-4 bg-slate-50 rounded-md" data-id="element-692">
            <div className="flex justify-between items-center mb-3" data-id="element-693">
              <h4 className="font-medium" data-id="element-694">Alternate Trustee {index + 1}</h4>
              {alternateTrustees.length > 1 && <button type="button" onClick={() => removeAlternateTrustee(trustee.id)} className="text-red-500 hover:text-red-700" data-id="element-695">
                  <TrashIcon size={16} data-id="element-696" />
                </button>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-697">
              <div data-id="element-698">
                <label className="block text-navy-800 font-medium mb-2" data-id="element-699">
                  Name
                </label>
                <input type="text" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500" placeholder="Enter alternate trustee's name" {...register(`estateTrustees.alternate.${index}.name`)} data-id="element-700" />
              </div>
              <div data-id="element-701">
                <label className="block text-navy-800 font-medium mb-2" data-id="element-702">
                  Relationship
                </label>
                <input type="text" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500" placeholder="e.g., Sibling, Friend" {...register(`estateTrustees.alternate.${index}.relationship`)} data-id="element-703" />
              </div>
              <div data-id="element-704">
                <label className="block text-navy-800 font-medium mb-2" data-id="element-705">
                  City
                </label>
                <input type="text" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500" placeholder="Enter city" {...register(`estateTrustees.alternate.${index}.city`)} data-id="element-706" />
              </div>
            </div>
          </div>)}
      </div>
    </div>;
};
export default ExistingEstateDocuments;